import {
  BasePageProps,
  UnknownObject,
  SafeRefetchProps,
  UseQueryReturnProps,
} from '../types';

import { joinStrings } from './helpers';
import { filterEmptyFields } from './object';

export const addParamsToUrl = (
  url: string,
  params?: Record<string, any>,
  options?: {
    preserveNull?: boolean;
  }
): string => {
  if (!params) {
    return url;
  }
  const urlParams = new URLSearchParams();
  const normalizedParams = options?.preserveNull
    ? Object.fromEntries(
        Object.entries(params).filter(([, value]) =>
          typeof value === 'boolean'
            ? true
            : value !== '' && value !== undefined
        )
      )
    : filterEmptyFields(params);

  Object.keys(normalizedParams).forEach((key) =>
    urlParams.append(key, normalizedParams[key])
  );
  return `${url}?${urlParams.toString()}`;
};

export const mapQuery = ({
  error,
  isFetching,
  ...props
}: UseQueryReturnProps): BasePageProps => {
  return {
    ...props,
    error: error?.message,
    isLoading: isFetching,
  };
};

export type Refetches<T> = Record<keyof T, () => void>;

export const safeRefetch = (props: SafeRefetchProps) => () => {
  const { refetch, isLoading, isUninitialized } = props;
  if (!isUninitialized && !isLoading) {
    try {
      refetch();
    } catch (error) {
      console.error(error);
      const sentry = import.meta.env.VITE_SENTRY;
      if (!sentry) {
        throw error;
      }
    }
  }
};

export interface MapQueriesReturnProps<T> extends BasePageProps {
  isFetching: boolean;
  refetchAll: () => void;
  refetches: Refetches<T>;
  isUninitialized: boolean;
}

export const mapQueries = <T extends UnknownObject>(
  queries: Record<keyof T, UseQueryReturnProps<T[keyof T]>>
): T & MapQueriesReturnProps<T> => {
  const dataProps = {} as T;
  const refetches = {} as Refetches<T>;

  const queriesState = Object.entries(queries).reduce<{
    errorStates: boolean[];
    loadingStates: boolean[];
    uninitializedStates: boolean[];
    errors: (string | undefined)[];
    refetchFunctions: (() => void)[];
  }>(
    (result, [name, query]) => {
      const { data, error, refetch, isError, isFetching, isUninitialized } =
        query;

      if (data) {
        dataProps[name as keyof T] = data;
      }

      refetches[name as keyof T] = refetch;

      result.loadingStates.push(isFetching);
      result.uninitializedStates.push(Boolean(isUninitialized));
      result.errorStates.push(isError);

      if (isError) {
        result.errors.push(error?.message);
        result.refetchFunctions.push(refetch);
      }

      return result;
    },
    {
      errors: [],
      errorStates: [],
      loadingStates: [],
      refetchFunctions: [],
      uninitializedStates: [],
    }
  );

  const isFetching = queriesState.loadingStates.some(Boolean);

  return {
    refetches,
    isFetching,
    isLoading: isFetching,
    isError: queriesState.errorStates.some(Boolean),
    isUninitialized: queriesState.uninitializedStates.some(Boolean),
    refetchAll: () =>
      Object.values(refetches).forEach((fn) => {
        try {
          fn();
        } catch (error) {
          console.error(error);
        }
      }),
    refetch: () =>
      queriesState.refetchFunctions.forEach((refetch) => {
        try {
          refetch();
        } catch (error) {
          console.error(error);
        }
      }),
    error:
      queriesState.errors.filter(Boolean).length > 0
        ? joinStrings(
            Array.from(new Set(queriesState.errors as string[])),
            '\n'
          )
        : undefined,
    ...dataProps,
  };
};
